import { useEffect, useState } from 'react';
import {
  IconVisa,
  IconMasterCard,
  IconLoader,
  SidePanel,
  SidePanelType,
  formatBalance,
  ButtonPrimary,
  ButtonPseudo,
  Popup,
} from '@ui/src';
import Card from '../card/card';
import { CardItemTypes } from './types';
import getCardStatusClassName from './helpers/get-card-status-name';
import {
  CardPaymentSystem,
  CardStatus,
  CardType,
} from '../../../hoc/common/cards/types';
import CardItemMenu from './parts/item-menu';
import CardItemLimitsForm from './parts/limits-form';

import cn from './styles.module.css';

const CardItem = ({
  card,
  onStatusChange,
  updatedCard,
  statusChangeInProgress,
  customerId,
  zIndex,
  changeStatusInProgress,
  changeStatusSuccess,
  customerType,
  activePermissions,
}: CardItemTypes) => {
  const [menuVisible, setMenuVisible] = useState(false);
  const [limitsPanelVisible, setLimitsPanelVisible] = useState(false);
  const [limits, setLimits] = useState({
    perMonth: '',
    perWeek: '',
    indefinitely: '',
  });
  const [confirmPopupVisible, setConfirmPopupVisible] = useState(false);
  const [statusForChange, setStatusForChange] = useState<CardStatus>();

  const cardDate = new Date(card.expiredDate);

  const disabledClassName =
    card.status === CardStatus.FROZEN ||
    card.status === CardStatus.CANCELED ||
    card.status === CardStatus.VOID;

  let icon = <div />;
  let paymentSystem = 'UnionPay';

  if (card.paymentSystem === CardPaymentSystem.MASTER_CARD) {
    icon = (
      <IconMasterCard
        className={`${cn.customerCardIcon} ${cn.customerCardIconMastercard}`}
        width={32}
        height={19}
      />
    );
    paymentSystem = 'MC';
  } else if (card.paymentSystem === CardPaymentSystem.VISA) {
    icon = (
      <IconVisa
        className={`${cn.customerCardIcon} ${cn.customerCardIconVisa}`}
        width={40}
        height={13}
      />
    );
    paymentSystem = 'VISA';
  }

  useEffect(() => {
    if (changeStatusSuccess) {
      setConfirmPopupVisible(false);
    }
  }, [changeStatusSuccess]);

  return (
    <>
      <Card className={cn.customerCard} zIndex={zIndex}>
        <div
          className={`${cn.customerCardTop} ${
            disabledClassName ? cn.customerCardTopDisabled : ''
          }`}
          id={card.id}
        >
          <div className={cn.customerCardTopLeft}>
            {icon}
            <div className={cn.customerCardType}>
              {`${paymentSystem} ${
                card.type === CardType.DEBIT
                  ? CardType.DEBIT
                  : `debit ${CardType.CORP}`
              }`}
            </div>
            <div className={cn.virtual}>
              {card.virtual ? 'Virtual' : 'Physical'}
            </div>
            <div className={cn.customerCardNumber}>{card.number}</div>
            <div className={cn.customerCardDate}>
              {`${(cardDate.getMonth() + 1)
                .toString()
                .padStart(2, '0')}/${cardDate.getFullYear()}`}
            </div>
          </div>
          <div className={cn.customerCardTopRight}>
            <div className={cn.customerCardBalance}>
              {`${
                card?.balance ? formatBalance(Number(card.balance)) : '-'
              } ${card.balanceCurrency.toUpperCase()}`}
            </div>
            <div
              className={`${cn.customerCardStatus} ${getCardStatusClassName(
                card.status,
              )}`}
            >
              {statusChangeInProgress && card.id === updatedCard?.id ? (
                <IconLoader width={20} height={20} />
              ) : (
                card.status
              )}
            </div>
            <CardItemMenu
              setMenuVisible={setMenuVisible}
              menuVisible={menuVisible}
              setLimitsPanelVisible={setLimitsPanelVisible}
              customerType={customerType}
              customerId={customerId}
              card={card}
              activePermissions={activePermissions}
              setConfirmPopupVisible={setConfirmPopupVisible}
              setStatusForChange={setStatusForChange}
            />
          </div>
        </div>
        <div className={cn.customerCardBottom}>
          <div className={cn.customerCardBottomLeft}>
            <div className={cn.customerCardBottomItem}>
              {card.customerAccountId}
            </div>
            <div className={cn.customerCardBottomItem}>
              {`${card.customerFirstName} ${card.customerLastName}`}
            </div>
          </div>
          <div className={cn.customerCardBottomRight}>
            <div className={cn.customerCardAddress}>{card.address}</div>
          </div>
        </div>
      </Card>
      <Popup
        title={`Change status to ${statusForChange}`}
        onCancelClick={() => setConfirmPopupVisible(false)}
        onOkClick={() =>
          onStatusChange({ id: card.id, status: statusForChange as CardStatus })
        }
        cancelButtonText="Cancel"
        okButtonText={changeStatusInProgress ? 'Loading...' : 'Change'}
        isVisible={confirmPopupVisible}
      />
      <SidePanel
        type={SidePanelType.Fees}
        closeOnClickOutside
        isOpen={limitsPanelVisible}
        onClose={() => setLimitsPanelVisible(false)}
        title="Limits"
        sidepanelExpandedKey="customers-cards"
        footer={
          <>
            <ButtonPrimary
              text="Save"
              onClick={() => setLimitsPanelVisible(false)}
              bold
            />
            <ButtonPseudo
              text="Close"
              onClick={() => setLimitsPanelVisible(false)}
              bold
            />
          </>
        }
      >
        <CardItemLimitsForm limits={limits} setLimits={setLimits} />
      </SidePanel>
    </>
  );
};

export default CardItem;
